import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { testimonials } from '@/mocks/data';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NTR大师',
  url: 'https://ntrmaster.com',
  description: '专业NTR内容创作与鉴赏指南平台，致力于为NTR爱好者与创作者提供深度、专业的内容服务。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ntrmaster.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpVal = (formData.get('mobile_alt') as string || '').trim();
    if (hpVal) {
      setFormStatus('success');
      return;
    }
    formData.delete('mobile_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9spbt5lbrt1qcgaj7gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (
        res.ok &&
        parsed &&
        typeof parsed === 'object' &&
        (parsed as Record<string, unknown>).code === 'OK'
      ) {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg =
          (parsed &&
            ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message) ||
          (parsed as Record<string, unknown>)?.message ||
          '';
        const msgStr = String(msg);
        if (text.includes('spam') || msgStr.includes('spam') || msgStr.includes('form data is spam')) {
          setFormError(msgStr || '提交失败，请稍后再试');
        } else {
          setFormError(msgStr || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 | NTR大师 - 专业NTR内容创作与鉴赏指南"
        description="了解NTR大师的品牌故事与使命，查看用户口碑评价，获取联系方式。NTR大师致力于为NTR爱好者与创作者提供最专业的内容服务。"
        keywords="NTR大师,关于NTR大师,NTR内容平台,NTR创作指南,联系我们"
        canonicalUrl="https://ntrmaster.com/about"
        schemaData={[organizationSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <div className="pt-24 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section id="brand" className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-4">
                关于NTR大师
              </h1>
              <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
                <p>
                  <strong className="text-foreground-200">NTR大师</strong>成立于2024年，是国内首个专注于NTR题材内容创作与鉴赏的垂直知识平台。我们汇聚了一批深耕ACGN文化领域的资深创作者和研究者，致力于为NTR爱好者与内容创作者提供最专业、最深度、最系统的内容服务。
                </p>
                <p>
                  在ACGN文化日益丰富的今天，NTR题材作为一个独特且具有深厚文化底蕴的叙事类型，亟需专业化的内容梳理与理论建设。<strong className="text-foreground-200">NTR大师</strong>正是基于这一需求应运而生——我们希望通过系统化的创作指南、深度的作品解析和多元的文化视角，帮助更多人理解、创作和鉴赏NTR内容。
                </p>
                <p>
                  我们的核心理念是<b>专业、深度、开放</b>。我们相信，NTR作为一种叙事形式，值得被认真对待和深入研究。无论您是刚接触NTR的新人，还是经验丰富的创作者，<strong className="text-foreground-200">NTR大师</strong>都将成为您最值得信赖的内容伙伴。
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4 rounded-lg bg-background-900 border border-background-800">
                  <div className="text-2xl font-bold text-primary-400 font-heading">69+</div>
                  <div className="text-xs text-foreground-500 mt-1">深度文章</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-900 border border-background-800">
                  <div className="text-2xl font-bold text-primary-400 font-heading">5</div>
                  <div className="text-xs text-foreground-500 mt-1">内容板块</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-900 border border-background-800">
                  <div className="text-2xl font-bold text-primary-400 font-heading">10K+</div>
                  <div className="text-xs text-foreground-500 mt-1">月访问用户</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20anime%20style%20illustration%20of%20a%20cozy%20library%20reading%20room%20with%20bookshelves%20and%20warm%20lamp%20light%2C%20contemplative%20mood%2C%20cel%20shaded%20art%20style%2C%20rich%20amber%20and%20dark%20wood%20tones%2C%20scholarly%20atmosphere&width=1200&height=800&seq=ntr-about-brand&orientation=landscape"
                alt="NTR大师品牌形象"
                title="NTR大师 - 专业NTR内容创作与鉴赏指南"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        <section id="testimonials" className="w-full bg-background-900 py-16 md:py-20 mt-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                用户口碑
              </h2>
              <p className="mt-2 text-foreground-400 text-sm">来自NTR大师用户的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="p-6 rounded-lg bg-background-950 border border-background-800"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-400 leading-relaxed mb-4">
                    "{t.content}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center">
                      <span className="text-primary-400 text-sm font-bold">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-300">{t.name}</div>
                      <div className="text-xs text-foreground-600">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
              联系我们
            </h2>
            <p className="mt-2 text-foreground-400 text-sm">
              有任何问题或合作意向，欢迎通过以下表单与我们联系
            </p>
          </div>

          <form
            data-readdy-form
            id="about-contact"
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm text-foreground-400 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-background-900 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/50 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm text-foreground-400 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 bg-background-900 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/50 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-sm text-foreground-400 mb-1.5">
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="w-full px-4 py-2.5 bg-background-900 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/50 transition-colors"
                placeholder="请输入咨询主题"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm text-foreground-400 mb-1.5">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                required
                maxLength={500}
                className="w-full px-4 py-2.5 bg-background-900 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/50 transition-colors resize-none"
                placeholder="请输入您的留言内容（500字以内）"
              />
              <p className="text-xs text-foreground-600 mt-1">最多500字符</p>
            </div>
            <input
              type="text"
              name="mobile_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="contact-honeypot"
            />
            <button
              type="submit"
              className="w-full py-3 bg-primary-500 text-background-950 font-medium rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              发送留言
            </button>
            {formStatus === 'success' && (
              <p className="text-center text-sm text-green-400">感谢您的留言！我们会尽快回复。</p>
            )}
            {formStatus === 'error' && formError && (
              <p className="text-center text-sm text-red-400">{formError}</p>
            )}
          </form>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-5 rounded-lg bg-background-900 border border-background-800 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-mail-line text-primary-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-300 mb-1 font-heading">电子邮箱</h3>
              <p className="text-xs text-foreground-500">contact@ntrmaster.com</p>
            </div>
            <div className="p-5 rounded-lg bg-background-900 border border-background-800 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-discord-line text-primary-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-300 mb-1 font-heading">Discord社区</h3>
              <p className="text-xs text-foreground-500">discord.gg/ntrmaster</p>
            </div>
            <div className="p-5 rounded-lg bg-background-900 border border-background-800 text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-twitter-x-line text-primary-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-300 mb-1 font-heading">社交媒体</h3>
              <p className="text-xs text-foreground-500">@NTRmaster</p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}